import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, X } from "lucide-react";
import { uploadPhoto, photoBlobUrl } from "../lib/api";

export const MAX_PHOTOS = 3;

export const PhotoThumb = ({ cid, pid, size = "h-20 w-20" }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u, on = true;
    photoBlobUrl(cid, pid).then((x) => { u = x; if (on) setUrl(x); }).catch(() => {});
    return () => { on = false; if (u) URL.revokeObjectURL(u); };
  }, [cid, pid]);
  return url
    ? <img src={url} alt="" data-testid={`photo-thumb-${pid}`} className={`${size} rounded-xl object-cover ring-1 ring-black/10`} />
    : <span className={`${size} grid place-items-center rounded-xl bg-slate-200/70`}><Loader2 size={16} className="animate-spin text-slate-400" /></span>;
};

// Upload-on-pick: photos are stored right away, then sent with the next message
export const usePhotoPicker = (cid, used, t) => {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const left = MAX_PHOTOS - used - pending.length;
  const add = async (files) => {
    const list = [...files].slice(0, Math.max(0, left));
    if (files.length > list.length) toast.info(t("photoLimit"));
    setBusy(true);
    for (const f of list) {
      try { const id = await uploadPhoto(cid, f); setPending((p) => [...p, id]); }
      catch { toast.error(t("photoError")); }
    }
    setBusy(false);
  };
  return { pending, busy, left, add, remove: (id) => setPending((p) => p.filter((x) => x !== id)), clear: () => setPending([]) };
};

export const AttachButton = ({ t, picker, disabled }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple hidden data-testid="photo-file-input"
        onChange={(e) => { picker.add(e.target.files); e.target.value = ""; }} />
      <button type="button" data-testid="attach-photo-btn" aria-label={t("attachPhoto")} title={picker.left > 0 ? t("attachPhoto") : t("photoLimit")}
        disabled={disabled || picker.busy || picker.left <= 0} onClick={() => ref.current?.click()}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-slate-200 text-teal-700 transition-colors hover:bg-teal-50 disabled:opacity-40">
        {picker.busy ? <Loader2 size={17} className="animate-spin" /> : <Camera size={18} />}
      </button>
    </>
  );
};

export const PendingPhotos = ({ t, cid, picker }) => picker.pending.length > 0 && (
  <div className="flex gap-2" data-testid="pending-photos">
    {picker.pending.map((pid) => (
      <div key={pid} className="relative">
        <PhotoThumb cid={cid} pid={pid} size="h-14 w-14" />
        <button type="button" data-testid={`remove-photo-${pid}`} aria-label={t("removePhoto")} onClick={() => picker.remove(pid)}
          className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-slate-800 text-white"><X size={11} /></button>
      </div>
    ))}
  </div>
);
